// ExamType — what kind of exam a course targets (法考 / 期末 / USMLE / 雅思 ...).
//
// 19BO+ P2-1 AGENTIC-DISCIPLINE-ROUTING (D-3 · Q1=A+B):
//   discipline (broad field enum) is too coarse to drive per-scenario rule
//   injection. A LAW course might target 法考 (statute precision), 期末/考研
//   (doctrinal display), or 英美法/IRAC (case citation) — each needs different
//   prompt rules. ExamType is the finer signal that lets route_discipline
//   pick the right rule subset instead of injecting all scenarios at once.
//
// Why a free string + a lookup table (not a single global enum):
//   - Each discipline has its OWN exam-type taxonomy. Law's 法考/IRAC makes no
//     sense for medicine; medicine's USMLE makes no sense for languages. A
//     single flat enum would mix unrelated values and bloat.
//   - ExamTypeSchema is deliberately loose (z.string) so the wire layer never
//     400s when a new discipline's values ship. The EXAM_TYPES_BY_DISCIPLINE
//     table is the display-layer source of truth — the create-form dropdown
//     reads it to render options per selected discipline.
//   - Adding a discipline's exam types (next session fills the remaining 7) is
//     a table extension, NOT a schema change. No breaking change, no version
//     bump for new values.
//
// Current coverage (P2-1 full fill · 2026-07-12):
//   - law:      3 values (bar_exam / final_postgrad / IRAC)       ✅ filled
//   - medicine: 2 values (licensing / USMLE)                      ✅ filled
//   - stem:     3 values (derivation / computation / conceptual)  ✅ filled
//   - humanities: 2 values (literary_analysis / historical_research) ✅ filled
//   - social_science: 2 values (quantitative / qualitative)       ✅ filled
//   - business: 2 values (strategic / financial)                  ✅ filled
//   - languages: 2 values (target_en / target_cn)                 ✅ filled
//   - arts:     2 values (formal_analysis / iconographic)         ✅ filled
//   - other:    [] (escape hatch — no fixed exam-type taxonomy)
//
// The examType value doubles as the scenario tag consumed by route_discipline
// (SCENARIO_BY_DISCIPLINE_EXAM_TYPE) and by buildDisciplineFormatRules
// (scenario sub-section selection). Renaming a value here breaks the route
// mapping + the rule scenario keys in discipline-profile.ts — keep them
// aligned (see 5-table alignment note in route-discipline.ts).
//
// Decision trace: docs/superpowers/plans/2026-06-30-agentic-discipline-routing.md §4.5
// Q1 (陛下 2026-07-12): 9 学科全加 examType.
import { z } from 'zod';
/**
 * Wire-layer schema for examType. Deliberately loose: any non-empty string
 * up to 60 chars. The display layer constrains options via
 * EXAM_TYPES_BY_DISCIPLINE; the wire layer must not reject values from
 * disciplines whose options ship in a later session.
 */
export const ExamTypeSchema = z.string().min(1).max(60);
/**
 * Per-discipline exam-type options. The create-form reads this to render the
 * examType dropdown for the selected discipline. An empty array means the
 * discipline's exam types are not yet filled — the form hides the dropdown
 * and examType stays undefined (route falls back to P1-1 static full rules).
 *
 * Values are stable string identifiers (NOT localized) so prompt rules and
 * routing logic can switch on them deterministically.
 */
export const EXAM_TYPES_BY_DISCIPLINE = {
    // ── LAW ─────────────────────────────────────────────────────────────────
    // Aligns with DISCIPLINE_FORMAT_RULES.statute_citation's three-scenario text
    // (法考/司考 · 期末/考研 · 英美法/IRAC). route_discipline maps examType →
    // which sub-scenario to activate.
    law: [
        {
            value: 'bar_exam',
            zh: '法考 / 司考',
            en: 'Bar Exam (PRC)',
            hint: {
                zh: '客观题答案须指明具体法条, 区分法定 / 拟制 / 注意规定',
                en: 'Objective items must cite the deciding article; distinguish statutory / fiction / notice provisions',
            },
        },
        {
            value: 'final_postgrad',
            zh: '期末 / 考研',
            en: 'Final / Postgrad Entrance',
            hint: {
                zh: '论述题须标明通说 / 少数说, 必要时标注学者或教材',
                en: 'Essays attribute majority/minority view (通说/少数说), cite scholar when load-bearing',
            },
        },
        {
            value: 'IRAC',
            zh: '英美法 / IRAC',
            en: 'Common Law / IRAC',
            hint: {
                zh: '范文按 Issue → Rule → Application → Conclusion, 引判例到裁判要旨',
                en: 'Model answer in IRAC structure; cite cases to the holding, not dictum',
            },
        },
    ],
    // ── MEDICINE ────────────────────────────────────────────────────────────
    // Aligns with DISCIPLINE_FORMAT_RULES.drug_nomenclature's two-scenario text
    // (执医/期末 · USMLE/英美).
    medicine: [
        {
            value: 'licensing',
            zh: '执医 / 期末',
            en: 'Medical Licensing / Final',
            hint: {
                zh: '病例题以诊断→用药为主线, INN 命名, 标剂量+途径+禁忌',
                en: 'Case items lead diagnosis→drug; INN naming; dose + route + contraindication',
            },
        },
        {
            value: 'USMLE',
            zh: 'USMLE / 英美',
            en: 'USMLE / Western',
            hint: {
                zh: '药品须配作用机制, 全程英文 INN',
                en: 'Pair drug with mechanism of action; English INN throughout',
            },
        },
    ],
    // ── STEM ────────────────────────────────────────────────────────────────
    // Aligns with DISCIPLINE_FORMAT_RULES.latex_escape (scenario-invariant,
    // preamble-only — all three examTypes activate the same rule; the scenario
    // tag still routes for future per-scenario specialization).
    stem: [
        {
            value: 'derivation',
            zh: '推导 / 证明',
            en: 'Derivation / Proof',
            hint: {
                zh: '定理推导与证明, LaTeX 公式精确, 每步注明依据',
                en: 'Theorem derivation and proof; precise LaTeX; justify each step',
            },
        },
        {
            value: 'computation',
            zh: '计算题',
            en: 'Computation',
            hint: {
                zh: '数值计算, 保留有效数字与单位, 中间步骤可验算',
                en: 'Numerical computation; keep significant figures + units; intermediate steps checkable',
            },
        },
        {
            value: 'conceptual',
            zh: '概念辨析',
            en: 'Conceptual',
            hint: {
                zh: '概念辨析与定性判断, 用形式定义区分易混概念',
                en: 'Conceptual discrimination; distinguish confusable concepts via formal definitions',
            },
        },
    ],
    // ── HUMANITIES ──────────────────────────────────────────────────────────
    // Aligns with text_evidence + historical_context. Scenario tag drives
    // citation style: literary_analysis → MLA; historical_research → Chicago.
    humanities: [
        {
            value: 'literary_analysis',
            zh: '文学分析',
            en: 'Literary Analysis',
            hint: {
                zh: 'MLA 引用, 文本细读, 作品斜体 / 短篇引号',
                en: 'MLA citation; close reading; italicize works, quote shorter pieces',
            },
        },
        {
            value: 'historical_research',
            zh: '史学研究',
            en: 'Historical Research',
            hint: {
                zh: 'Chicago 注释-书目制, 区分一手 / 二手史料, 锚定年代',
                en: 'Chicago notes-bibliography; distinguish primary/secondary sources; anchor dates',
            },
        },
    ],
    // ── SOCIAL_SCIENCE ──────────────────────────────────────────────────────
    // Aligns with empirical_citation + quantitative_report. APA author-date.
    social_science: [
        {
            value: 'quantitative',
            zh: '定量研究',
            en: 'Quantitative',
            hint: {
                zh: 'APA 引用, 报告效应量 + CI, p 值精确到 2-3 位',
                en: 'APA citation; report effect size + CI; p-values to 2–3 decimals',
            },
        },
        {
            value: 'qualitative',
            zh: '定性研究',
            en: 'Qualitative',
            hint: {
                zh: 'APA 引用, 厚描述 + 参与者原话, 编码频次精确',
                en: 'APA citation; thick description + participant voice; precise code frequencies',
            },
        },
    ],
    // ── BUSINESS ────────────────────────────────────────────────────────────
    // Aligns with case_framework + financial_precision.
    business: [
        {
            value: 'strategic',
            zh: '战略分析',
            en: 'Strategic Analysis',
            hint: {
                zh: '点名框架 (SWOT/波特五力), 区分行业 vs 企业层面',
                en: 'Name the framework (SWOT/Porter); distinguish industry vs firm level',
            },
        },
        {
            value: 'financial',
            zh: '财务分析',
            en: 'Financial Analysis',
            hint: {
                zh: '比率标注公式 + 期间, 金额带币种单位, 区分存量 / 流量',
                en: 'Ratios with formula + period; money with currency/unit; stock vs flow',
            },
        },
    ],
    // ── LANGUAGES ───────────────────────────────────────────────────────────
    // Aligns with transliteration. Direction of translation determines the rule.
    languages: [
        {
            value: 'target_en',
            zh: '英文为目标语',
            en: 'English as Target',
            hint: {
                zh: '考研 / 雅思 / TOEFL, 习语准确性 + 语域控制',
                en: 'Postgrad / IELTS / TOEFL; idiom accuracy + register control',
            },
        },
        {
            value: 'target_cn',
            zh: '中文为目标语',
            en: 'Chinese as Target',
            hint: {
                zh: 'HSK / 对外汉语, 首次标注拼音, 正确量词, 母语词序',
                en: 'HSK / CFL; pinyin on first mention; correct 量词; native word order',
            },
        },
    ],
    // ── ARTS ────────────────────────────────────────────────────────────────
    // Aligns with visual_analysis + movement_context.
    arts: [
        {
            value: 'formal_analysis',
            zh: '形式分析',
            en: 'Formal Analysis',
            hint: {
                zh: '形式元素描述 (线/色/构图), 每选择扣连视觉效果',
                en: 'Describe via formal elements (line/color/composition); connect to visual effect',
            },
        },
        {
            value: 'iconographic',
            zh: '图像学分析',
            en: 'Iconographic Analysis',
            hint: {
                zh: '辨识符号约定含义, 引图像学来源, 区分作者意图与后世解读',
                en: 'Identify conventional symbols; cite iconographic source; distinguish intent vs later reading',
            },
        },
    ],
    // ── OTHER ───────────────────────────────────────────────────────────────
    // Escape hatch — no fixed exam-type taxonomy. examType stays undefined;
    // route falls back to P1-1 static full-rule injection (or customDiscipline
    // LLM classification if customDiscipline is set).
    other: [],
};
/**
 * Whether a discipline has exam-type options filled yet. The create-form uses
 * this to decide whether to render the examType dropdown.
 */
export function disciplineHasExamTypes(discipline) {
    return EXAM_TYPES_BY_DISCIPLINE[discipline].length > 0;
}
/**
 * Look up the exam-type options for a discipline. Returns empty readonly
 * array if none filled yet (defensive — callers should check
 * disciplineHasExamTypes first for UI rendering).
 */
export function examTypesForDiscipline(discipline) {
    return EXAM_TYPES_BY_DISCIPLINE[discipline];
}
//# sourceMappingURL=exam-type.js.map